import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../pay/PayOrderPage.css";
import "./PaymentTipPage.css";

const BASE_URL = "https://fooapp.mx";
const API_URL = (process.env.REACT_APP_API_URL || "https://api.fooapp.mx").replace(/\/graphql\/?$/, "");

interface PaymentLinkData {
  id: number;
  amount: number;
  description: string;
  status: string;
  partner_name: string;
  partner_logo: string | null;
  expires_at: string | null;
}

function formatPrice(amount: number): string {
  return amount.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
  });
}

const TIP_OPTIONS = [0, 5, 10, 15, 20];

const PaymentTipPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const [data, setData] = useState<PaymentLinkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tipPercent, setTipPercent] = useState(0);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!linkId) {
      setError("not_found");
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/api/payment-link/${linkId}`)
      .then((res) => {
        if (res.status === 404) throw new Error("not_found");
        if (res.status === 410) throw new Error("expired");
        if (!res.ok) throw new Error("error");
        return res.json();
      })
      .then((d: PaymentLinkData) => {
        if (d.status === "paid") {
          setError("already_paid");
        } else if (d.status === "expired" || d.status === "cancelled") {
          setError("expired");
        }
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "error");
        setLoading(false);
      });
  }, [linkId]);

  const tipAmount = data ? Math.round(data.amount * (tipPercent / 100) * 100) / 100 : 0;
  const total = data ? data.amount + tipAmount : 0;

  const handlePay = async () => {
    if (!linkId) return;
    setRedirecting(true);
    try {
      const res = await fetch(`${API_URL}/api/payment-link/${linkId}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tip_percent: tipPercent }),
      });
      if (!res.ok) throw new Error("checkout_error");
      const result = await res.json();
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        throw new Error("checkout_error");
      }
    } catch {
      setRedirecting(false);
      setError("checkout_error");
    }
  };

  const renderExpired = () => (
    <div className="pay-body">
      <div className="pay-status-icon warning">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4m0 4h.01" />
        </svg>
      </div>
      <h2>Link expirado</h2>
      <p className="subtitle">Este link de cobro ha expirado o fue cancelado.</p>
    </div>
  );

  const renderAlreadyPaid = () => (
    <div className="pay-body">
      <div className="pay-status-icon success">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2>Este cobro ya fue pagado</h2>
      <p className="subtitle">El pago ya fue procesado correctamente.</p>
    </div>
  );

  const renderNotFound = () => (
    <div className="pay-body">
      <div className="pay-status-icon error">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2>Link no encontrado</h2>
      <p className="subtitle">El enlace de cobro no es valido.</p>
    </div>
  );

  const renderCheckoutError = () => (
    <div className="pay-body">
      <div className="pay-status-icon error">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2>Error al procesar el pago</h2>
      <p className="subtitle">Ocurrio un error al iniciar el pago. Intenta de nuevo.</p>
      <button className="pay-cta" onClick={() => setError(null)}>Reintentar</button>
    </div>
  );

  const renderPayment = (d: PaymentLinkData) => {
    const logoUrl = d.partner_logo
      ? d.partner_logo.startsWith("http") ? d.partner_logo : `${BASE_URL}${d.partner_logo}`
      : null;

    return (
      <>
        <div className="pay-body">
          <div className="pay-restaurant">
            {logoUrl && (
              <img src={logoUrl} alt={d.partner_name} className="pay-restaurant-logo" />
            )}
            <h3 className="pay-restaurant-name">{d.partner_name}</h3>
            <p className="pay-order-id">{d.description}</p>
          </div>

          {/* Amount */}
          <div className="pay-summary">
            <div className="pay-summary-row">
              <span className="pay-summary-label">Cobro</span>
              <span className="pay-summary-value">{formatPrice(d.amount)}</span>
            </div>
          </div>

          {/* Tip selector */}
          <div className="tip-section">
            <p className="tip-label">¿Gustas agregar propina?</p>
            <div className="tip-options">
              {TIP_OPTIONS.map((pct) => (
                <button
                  key={pct}
                  className={`tip-btn ${tipPercent === pct ? "tip-btn-active" : ""}`}
                  onClick={() => setTipPercent(pct)}
                >
                  {pct === 0 ? "Sin propina" : `${pct}%`}
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="pay-summary">
            <div className="pay-summary-row">
              <span className="pay-summary-label">Subtotal</span>
              <span className="pay-summary-value">{formatPrice(d.amount)}</span>
            </div>
            {tipAmount > 0 && (
              <div className="pay-summary-row">
                <span className="pay-summary-label">Propina ({tipPercent}%)</span>
                <span className="pay-summary-value">{formatPrice(tipAmount)}</span>
              </div>
            )}
            <div className="pay-summary-row total">
              <span className="pay-summary-label">Total</span>
              <span className="pay-summary-value">{formatPrice(total)}</span>
            </div>
          </div>

          <button
            className="pay-cta"
            onClick={handlePay}
            disabled={redirecting}
          >
            {redirecting ? (
              <span className="spinner-inline" />
            ) : (
              `Pagar ${formatPrice(total)}`
            )}
          </button>
        </div>

        <div className="pay-footer">
          <p>
            El pago sera procesado de forma segura a traves de Stripe.
            <br />
            Si tienes alguna duda, contacta directamente a {d.partner_name}.
          </p>
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="pay-loading">
          <div className="spinner" />
          <p style={{ color: "#888" }}>Cargando...</p>
        </div>
      );
    }
    if (error === "checkout_error" && data) return renderCheckoutError();
    if (error === "already_paid") return renderAlreadyPaid();
    if (error === "expired") return renderExpired();
    if (error === "not_found" || !data) return renderNotFound();
    if (data.status !== "pending") return renderExpired();
    return renderPayment(data);
  };

  return (
    <>
      <Helmet>
        <title>{data?.partner_name ? `Pagar - ${data.partner_name} | Foo` : "Pagar | Foo"}</title>
        <meta name="theme-color" content="#292929" />
      </Helmet>
      <div className="pay-container">
        <div className="pay-card">
          <div className="pay-header">
            <img src={`${BASE_URL}/img/logo_svg.svg`} alt="Foo" />
          </div>
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default PaymentTipPage;
